import { FormLabelRAC, FieldErrors } from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import get from "lodash/get";
import { useCallback, useContext, useEffect } from "react";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { Input } from "../components/Input";
import { InputModal } from "@certn/cascadia-react";
import { SummaryItem } from "../components/SummaryItem";
import { Switch } from "../components/Switch";
import { Text } from "../components/Text";
import { TextField } from "../components/TextField";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

interface OptOutToggleOption {
  /** Root-level data path of the boolean the toggle writes, e.g. "has_no_additional_name" */
  path: string;
  label: string;
}

export function TextFieldControl(props: CascadiaControlProps) {
  const { options, description, path } = props;
  const optOutToggle = options.optOutToggle as OptOutToggleOption | undefined;

  useEffect(() => {
    if (options.initialValue && !props.data) {
      props.setValue(options.initialValue);
    }
  }, [props.data, props.setValue, options.initialValue]);

  const { formID, isSummaryForPath, values } = useContext(
    CascadiaJsonFormContext
  );

  const isOptedOut = optOutToggle
    ? get(values, optOutToggle.path) === true
    : false;

  // Turning the toggle on must also empty the field: a disabled field still
  // submits its value, which would contradict the toggle on the backend. The
  // input is disabled here rather than by a ui_schema rule, because an element
  // rule would override the parent's enabled state in JSON Forms.
  const handleOptOutChange = useCallback(
    (isSelected: boolean) => {
      if (!optOutToggle) {
        return;
      }
      props.handleChange(optOutToggle.path, isSelected);
      if (isSelected) {
        props.setValue("");
      }
    },
    [optOutToggle?.path, props.handleChange, props.setValue]
  );

  const summaryData = (): string | undefined => {
    if (!props.data) {
      return undefined;
    }
    if (options.redact_summary) {
      const halfLength = Math.ceil(props.data.length / 2);
      const replaceRegex = new RegExp("^.{" + halfLength + "}");
      return props.data.replace(replaceRegex, "*".repeat(halfLength));
    }
    return props.data;
  };

  const handleBlur = useCallback(() => {
    // Remove whitespace at the end of the string
    if (typeof props.data === "string" || props.data instanceof String) {
      if (props.data.endsWith(" ")) {
        props.setValue(props.data.trim());
      }
    }
    props.setTouched();
  }, [props.setValue]);

  if (
    isSummaryForPath?.(path) &&
    // some fields are displayed as readonly text boxes rather than summary items
    !props.options?.renderSummaryAsReadOnly
  ) {
    return (
      <SummaryItem label={props.label} labelAlign={options.labelAlign}>
        {summaryData()}
      </SummaryItem>
    );
  }

  // without formatting we'd get eg: names.0.given-name
  const inputName = slugify(props.path).split(".").pop();

  const textField = (
    <TextField
      isDisabled={!props.enabled || isSummaryForPath?.(path) || isOptedOut}
      isInvalid={props.invalid}
      isRequired={props.required}
      maxLength={props.schema.maxLength}
      minLength={props.schema.minLength}
      name={props.path}
      onBlur={handleBlur}
      onChange={props.setValue}
      pattern={props.schema.pattern}
      type={props.schema.format}
      value={props.data || ""}
      labelAlign={options.labelAlign}
    >
      <div className={styles.wrapper}>
        {props.label && (
          <FormLabelRAC
            info={options.info}
            optionalText={options.optionalText}
            isRequired={props.required}
          >
            {props.label}
          </FormLabelRAC>
        )}

        <InputModal {...options.more_info} />
      </div>
      <Input
        placeholder={options.placeholder}
        data-testid={
          formID ? `${formID}-${inputName}-input` : `${inputName}-input`
        }
      />
      {description && (
        <div className={styles.wrapper}>
          <Text style="input/helper" color="text-weak" slot="description">
            {description}
          </Text>
          {options.multiline === true && props.schema.maxLength && (
            <Text style="input/helper" color="text-weak" slot="description">
              {`${props.data?.length ?? 0}/${props.schema.maxLength}`}
            </Text>
          )}
        </div>
      )}
      {props.touched && <FieldErrors>{props.errors}</FieldErrors>}
    </TextField>
  );

  if (!optOutToggle) {
    return textField;
  }

  // Rendered outside the TextField so react-aria's field slots don't bind to it.
  return (
    <div className={styles.optOutWrapper}>
      {textField}
      <Switch
        isSelected={isOptedOut}
        onChange={handleOptOutChange}
        isDisabled={!props.enabled}
        testID={
          formID
            ? `${formID}-${slugify(optOutToggle.path)}-toggle`
            : `${slugify(optOutToggle.path)}-toggle`
        }
      >
        {optOutToggle.label}
      </Switch>
    </div>
  );
}

const styles = {
  wrapper: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  optOutWrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
};

TextFieldControl.displayName = "TextFieldControl";

export const TextFieldControlRenderer =
  withCascadiaControlProps(TextFieldControl);
